/**
 * Vehicles Hook
 * React hook járművek kezeléséhez
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Vehicle, VehicleFormData, VehicleStats, FleetDashboard } from '../types/vehicle';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  getVehicles,
  getVehicle,
  createVehicle,
  updateVehicle,
  deleteVehicle,
  updateVehicleMileage,
  getVehicleStats,
  getFleetDashboard
} from '../services/vehicleService';

export function useVehicles(status?: string) {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener for vehicles
  useEffect(() => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const vehiclesRef = collection(db, 'vehicles');
    const constraints = [
      where('organizationId', '==', user.organizationId),
      orderBy('licensePlate', 'asc')
    ];

    if (status) {
      constraints.push(where('status', '==', status));
    }

    const q = query(vehiclesRef, ...constraints);

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const vehiclesData = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          purchaseDate: doc.data().purchaseDate?.toDate(),
          nextServiceDate: doc.data().nextServiceDate?.toDate(),
          insuranceExpiryDate: doc.data().insuranceExpiryDate?.toDate(),
          inspectionExpiryDate: doc.data().inspectionExpiryDate?.toDate(),
          documents: doc.data().documents?.map((d: any) => ({
            ...d,
            uploadedAt: d.uploadedAt?.toDate() || new Date(),
            expiryDate: d.expiryDate?.toDate()
          })) || [],
          photos: doc.data().photos?.map((p: any) => ({
            ...p,
            uploadedAt: p.uploadedAt?.toDate() || new Date()
          })) || []
        })) as Vehicle[];

        // Denormalizáljuk a hiányzó customerName mezőket
        const vehiclesWithNames = await Promise.all(
          vehiclesData.map(async (vehicle) => {
            if (vehicle.customerId && !vehicle.customerName) {
              try {
                const { getDoc, doc } = await import('firebase/firestore');
                const orgDoc = await getDoc(doc(db, 'organizations', vehicle.customerId));
                if (orgDoc.exists()) {
                  return {
                    ...vehicle,
                    customerName: orgDoc.data().name
                  };
                }
              } catch (error) {
                console.error('Error fetching organization name:', error);
              }
            }
            return vehicle;
          })
        );

        setVehicles(vehiclesWithNames);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message || 'Hiba a járművek betöltése során');
        console.error('Error loading vehicles:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.organizationId, status]);

  // Járművek betöltése (manual refresh)
  const loadVehicles = useCallback(async () => {
    if (!user?.organizationId) {
      return;
    }

    try {
      setLoading(true);
      const data = await getVehicles(user.organizationId, status);
      setVehicles(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Hiba a járművek betöltése során');
      console.error('Error loading vehicles:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.organizationId, status]);

  // Új jármű létrehozása
  const create = useCallback(
    async (vehicleData: VehicleFormData): Promise<string> => {
      if (!user?.organizationId || !user?.uid) {
        throw new Error('Nincs bejelentkezve');
      }

      try {
        const vehicleId = await createVehicle(vehicleData, user.organizationId, user.uid);
        return vehicleId;
      } catch (err: any) {
        setError(err.message || 'Hiba a jármű létrehozása során');
        throw err;
      }
    },
    [user?.organizationId, user?.uid]
  );

  // Jármű frissítése
  // Megjegyzés: Bármilyen Vehicle mező frissíthető, nem csak VehicleFormData mezők
  const update = useCallback(
    async (vehicleId: string, updates: Partial<Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'organizationId' | 'documents' | 'photos'>>): Promise<void> => {
      try {
        await updateVehicle(vehicleId, updates);
      } catch (err: any) {
        setError(err.message || 'Hiba a jármű frissítése során');
        throw err;
      }
    },
    []
  );

  // Jármű törlése
  const remove = useCallback(async (vehicleId: string): Promise<void> => {
    try {
      await deleteVehicle(vehicleId);
    } catch (err: any) {
      setError(err.message || 'Hiba a jármű törlése során');
      throw err;
    }
  }, []);

  // Km óra frissítése
  const updateMileage = useCallback(
    async (vehicleId: string, mileage: number): Promise<void> => {
      try {
        await updateVehicleMileage(vehicleId, mileage);
      } catch (err: any) {
        setError(err.message || 'Hiba a km óra frissítése során');
        throw err;
      }
    },
    []
  );

  return {
    vehicles,
    loading,
    error,
    loadVehicles,
    create,
    update,
    remove,
    updateMileage
  };
}

// Hook egy adott jármű lekéréséhez
export function useVehicle(vehicleId: string | null) {
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!vehicleId) {
      setVehicle(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const vehicleRef = collection(db, 'vehicles');
    const q = query(vehicleRef, where('__name__', '==', vehicleId));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (snapshot.empty) {
          setVehicle(null);
          setLoading(false);
          return;
        }

        const doc = snapshot.docs[0];
        const vehicleData = {
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          purchaseDate: doc.data().purchaseDate?.toDate(),
          nextServiceDate: doc.data().nextServiceDate?.toDate(),
          insuranceExpiryDate: doc.data().insuranceExpiryDate?.toDate(),
          inspectionExpiryDate: doc.data().inspectionExpiryDate?.toDate(),
          documents: doc.data().documents?.map((d: any) => ({
            ...d,
            uploadedAt: d.uploadedAt?.toDate() || new Date(),
            expiryDate: d.expiryDate?.toDate()
          })) || [],
          photos: doc.data().photos?.map((p: any) => ({
            ...p,
            uploadedAt: p.uploadedAt?.toDate() || new Date()
          })) || []
        } as Vehicle;

        setVehicle(vehicleData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message || 'Hiba a jármű betöltése során');
        console.error('Error loading vehicle:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [vehicleId]);

  return { vehicle, loading, error };
}

// Hook flotta statisztikákhoz
export function useVehicleStats() {
  const { user } = useAuth();
  const [stats, setStats] = useState<VehicleStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const statsData = await getVehicleStats(user.organizationId);
      setStats(statsData);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Hiba a statisztikák betöltése során');
      console.error('Error loading vehicle stats:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.organizationId]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return { stats, loading, error, refresh: loadStats };
}

// Hook flotta dashboard-hoz
export function useFleetDashboard() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState<FleetDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const dashboardData = await getFleetDashboard(user.organizationId);
      setDashboard(dashboardData);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Hiba a dashboard betöltése során');
      console.error('Error loading fleet dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.organizationId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  return { dashboard, loading, error, refresh: loadDashboard };
}
